import React from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { designSystem } from '../../../styles/design-system';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ChartContainer = styled.div`
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.xl};
  padding: ${designSystem.spacing.xl};
  margin-bottom: ${designSystem.spacing.xl};
  border: 1px solid ${designSystem.colors.border.light};
  
  h3 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize.xl};
    margin-bottom: ${designSystem.spacing.lg};
    text-align: center;
  }
  
  .chart-wrapper {
    position: relative;
    height: 300px;
    
    @media (max-width: ${designSystem.breakpoints.tablet}) {
      height: 250px;
    }
  }
`;

const ChartControls = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  margin-bottom: ${designSystem.spacing.lg};
  justify-content: center;
  flex-wrap: wrap;
`;

const ChartButton = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? designSystem.colors.primary.main
    : 'transparent'};
  color: ${props => props.active 
    ? '#000000' 
    : designSystem.colors.text.secondary};
  border: 2px solid ${designSystem.colors.primary.main};
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  
  &:hover {
    background: ${designSystem.colors.primary.main};
    color: #000000;
  }
`;

interface BookingData {
  date: string;
  bookings: number;
  revenue: number;
  confirmed: number;
  cancelled: number;
}

interface BookingChartProps {
  data: BookingData[];
  chartType: 'line' | 'bar';
  onChartTypeChange: (type: 'line' | 'bar') => void;
}

const BookingChart: React.FC<BookingChartProps> = ({ 
  data, 
  chartType, 
  onChartTypeChange 
}) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: designSystem.colors.text.primary,
          font: {
            family: designSystem.typography.fontFamily.primary,
            size: 12,
          }
        }
      },
      tooltip: {
        backgroundColor: designSystem.colors.background.primary,
        titleColor: designSystem.colors.text.primary,
        bodyColor: designSystem.colors.text.secondary,
        borderColor: designSystem.colors.border.light,
        borderWidth: 1,
        cornerRadius: 8,
      }
    },
    scales: {
      x: {
        grid: {
          color: designSystem.colors.border.light,
        },
        ticks: {
          color: designSystem.colors.text.secondary,
          font: {
            family: designSystem.typography.fontFamily.primary,
            size: 11,
          }
        }
      },
      y: {
        grid: {
          color: designSystem.colors.border.light,
        },
        ticks: {
          color: designSystem.colors.text.secondary,
          font: {
            family: designSystem.typography.fontFamily.primary,
            size: 11,
          }
        }
      }
    }
  };

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Prenotazioni Totali',
        data: data.map(d => d.bookings),
        borderColor: designSystem.colors.primary.main,
        backgroundColor: designSystem.colors.primary.main + '20',
        fill: chartType === 'line',
        tension: 0.4,
      },
      {
        label: 'Confermate',
        data: data.map(d => d.confirmed),
        borderColor: designSystem.colors.status.success,
        backgroundColor: designSystem.colors.status.success + '20',
        fill: false,
        tension: 0.4,
      },
      {
        label: 'Cancellate',
        data: data.map(d => d.cancelled),
        borderColor: designSystem.colors.status.error,
        backgroundColor: designSystem.colors.status.error + '20',
        fill: false,
        tension: 0.4,
      }
    ]
  };

  const revenueData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Revenue (€)',
        data: data.map(d => d.revenue),
        borderColor: designSystem.colors.status.success,
        backgroundColor: designSystem.colors.status.success + '30',
        fill: chartType === 'line',
        tension: 0.4,
      }
    ]
  };

  return (
    <>
      <ChartContainer>
        <h3>📊 Trend Prenotazioni</h3>
        <ChartControls>
          <ChartButton 
            active={chartType === 'line'}
            onClick={() => onChartTypeChange('line')}
          >
            📈 Linea
          </ChartButton>
          <ChartButton 
            active={chartType === 'bar'}
            onClick={() => onChartTypeChange('bar')}
          >
            📊 Barre
          </ChartButton>
        </ChartControls>
        <div className="chart-wrapper">
          {chartType === 'line' ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </ChartContainer>

      <ChartContainer>
        <h3>💰 Revenue nel Tempo</h3>
        <div className="chart-wrapper">
          {chartType === 'line' ? (
            <Line data={revenueData} options={chartOptions} />
          ) : (
            <Bar data={revenueData} options={chartOptions} />
          )}
        </div>
      </ChartContainer>
    </>
  );
};

export default BookingChart; 